// Port web de expo-secure-store.
//
// ⚠️ CHANGEMENT DE MODÈLE DE SÉCURITÉ — à lire avant de toucher ce fichier.
//
// Sur natif, les tokens vivaient dans le Keychain iOS / Keystore Android :
// chiffrés par l'OS, inaccessibles au code JS d'un autre processus.
// Le navigateur n'offre aucun équivalent. Tout ce qui est ici est lisible par
// n'importe quel script s'exécutant sur l'origine — donc par une XSS.
//
// Contreparties retenues (cf. MIGRATION_WEB.md §2) :
//   • sessionStorage et non localStorage : la session meurt à la fermeture de
//     l'onglet et n'est pas partagée entre onglets, ce qui réduit la fenêtre
//     d'exposition. Conséquence assumée : plus de « rester connecté » entre
//     deux sessions de navigateur.
//   • CSP stricte obligatoire au déploiement — c'est la vraie mitigation.
//
// L'INTERFACE (saveTokens/loadTokens/clearTokens) est identique à celle du
// natif. C'est délibéré : le passage à un BFF avec cookie httpOnly ne doit
// toucher que ce fichier.
//
// La limite de 2 048 octets du Keystore Android n'existe plus, mais on garde
// une clé par token — l'interface publique en dépend.

const ACCESS_KEY = "sosmain.access_token";
const REFRESH_KEY = "sosmain.refresh_token";
const ID_KEY = "sosmain.id_token";

export type StoredTokens = {
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
};

// Un navigateur en navigation privée stricte, ou avec le stockage bloqué, jette
// sur l'accès à sessionStorage. On dégrade silencieusement plutôt que de casser
// le boot : l'utilisateur devra se reconnecter, ce qui reste fonctionnel.
function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* stockage indisponible — best effort */
  }
}

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* stockage indisponible — best effort */
  }
}

// Les fonctions restent `async` alors que sessionStorage est synchrone : les
// appelants (sessionStore, keycloakAuthService) les attendent en promesse.
export async function saveTokens(tokens: StoredTokens): Promise<void> {
  // On supprime plutôt que d'écrire une chaîne vide, pour que loadTokens()
  // renvoie bien `undefined` sur un token absent.
  if (tokens.accessToken) safeSet(ACCESS_KEY, tokens.accessToken);
  else safeRemove(ACCESS_KEY);

  if (tokens.refreshToken) safeSet(REFRESH_KEY, tokens.refreshToken);
  else safeRemove(REFRESH_KEY);

  if (tokens.idToken) safeSet(ID_KEY, tokens.idToken);
  else safeRemove(ID_KEY);
}

export async function loadTokens(): Promise<StoredTokens> {
  return {
    accessToken: safeGet(ACCESS_KEY) ?? undefined,
    refreshToken: safeGet(REFRESH_KEY) ?? undefined,
    idToken: safeGet(ID_KEY) ?? undefined,
  };
}

export async function clearTokens(): Promise<void> {
  safeRemove(ACCESS_KEY);
  safeRemove(REFRESH_KEY);
  safeRemove(ID_KEY);
}
